import React, { useRef } from 'react';
import { useOverlayTrigger, useModalOverlay, Overlay, FocusScope } from 'react-aria';
import { useTransition, animated } from 'react-spring';
import styled, { useTheme, ThemeProvider } from 'styled-components';
import { ButtonIcon, tokens } from '@happy-design/react';
import { useExperienceSwitcher } from './ExperienceSwitcherContext.js';

const ExperienceNavIcon = styled(ButtonIcon)`
  background-color: transparent;
  color: ${tokens.color.palette.white[100]};
  font-size: 1.15rem;

  height: 2rem;
  min-width: 2rem;

  margin-right: 0.25rem;
  &:hover,
  &:active,
  &:focus {
    background-color: transparent;
  }
  &:hover > * {
    opacity: 0.8;
  }
`;
const Modal = (props)=>{
    const uxs = useExperienceSwitcher();
    const triggerRef = useRef();
    const { triggerProps , overlayProps  } = useOverlayTrigger({
        type: "dialog"
    }, uxs.modalState, triggerRef);
    const modalRef = useRef();
    const { modalProps , underlayProps  } = useModalOverlay({
        // Disable the default dismiss behaviour so that the internal popups/menus don't trigger
        isDismissable: false
    }, uxs.modalState, modalRef);
    const theme = useTheme();
    const transition = useTransition(uxs.modalState.isOpen, {
        from: {
            x: -22,
            overlayOpacity: 0
        },
        enter: {
            x: 0,
            overlayOpacity: 0.3
        },
        leave: {
            x: -22,
            overlayOpacity: 0,
            onResolve: uxs.resetSwitcher
        },
        delay: 200
    });
    // Extract onPress to suppress style component warnings
    const { onPress: _ , ...componentProps } = triggerProps;
    const trigger = props.trigger ? React.cloneElement(props.trigger, {
        ...triggerProps,
        onClick: triggerProps.onPress(undefined)
    }) : React.createElement(ExperienceNavIcon, {
        "data-hd-nav-exp-switcher": true,
        testId: "ExperienceSwitcher",
        icon: "grid",
        ...componentProps,
        onClick: ()=>triggerProps.onPress(undefined)
    });
    return React.createElement(React.Fragment, null, trigger, transition(({ x , overlayOpacity  }, item)=>item && React.createElement(Overlay, {
            ...overlayProps
        }, React.createElement("div", {
            style: {
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start"
            },
            "aria-hidden": true
        }, React.createElement(animated.div, {
            ...underlayProps,
            style: {
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                background: tokens.color.palette.blurple[160],
                opacity: overlayOpacity.to((o)=>o),
                zIndex: tokens.layer["z-index"].overlay,
                ...underlayProps.style
            },
            "data-test-id": "ExperienceSwitcher-ModalUnderlay",
            onClick: ()=>uxs.modalState.close()
        }), React.createElement(animated.div, {
            ...modalProps,
            ref: modalRef,
            style: {
                height: "100%",
                transform: x.to((x)=>`translateX(${x}rem)`),
                zIndex: tokens.layer["z-index"].modal
            },
            "data-test-id": "ExperienceSwitcher-Modal"
        }, React.createElement(ThemeProvider, {
            theme: {
                ...theme,
                PORTAL_LAYER: 700
            }
        }, React.createElement(FocusScope, {
            contain: true,
            restoreFocus: true,
            autoFocus: true
        }, props.children)))))));
};

export { Modal };
